// Sugalarm — Settings → Display → "Alarm clock mode" card (cycle 145).
//
// Start-now (manual session with a duration) + the persisted nightly
// schedule (enable switch, start/end times, 7-day toggles). The
// schedule lives in the additive `alarm_clock` settings key; every
// change PATCHes the WHOLE sub-object (the server validates HH:MM +
// exactly-7-days), and the change rides the existing `settings_update`
// broadcast so every device's schedule flips live.
//
// Kiosk has no OS keyboard — time entry there is a keyboard-free
// hour/minute stepper; mobile/web use a native <input type="time">.

import type { FrameVariant } from "../../hooks/useFrameVariant";
import { hapticSelect } from "../../lib/haptics";
import type { AlarmClockSchedule, UserSettingsPatch } from "../../lib/types";
import { SubsectionCard } from "./SubsectionCard";
import { Row, Toggle } from "./SettingsScreen";
import styles from "./SettingsScreen.module.css";

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]; // index 0 = Sunday

const ClockIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 8 v4 l3 2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function fmtTimeLabel(hhmm: string, h24: boolean): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (h == null || m == null || Number.isNaN(h) || Number.isNaN(m)) {
    return hhmm;
  }
  if (h24) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

function shiftTime(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (h == null || m == null) return hhmm;
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
    total % 60,
  ).padStart(2, "0")}`;
}

/** Kiosk keyboard-free time field: ‹ › steppers (hour / 15-min). */
function KioskTimeStepper({
  value,
  h24,
  onChange,
}: {
  value: string;
  h24: boolean;
  onChange: (next: string) => void;
}) {
  const btn = (label: string, delta: number, aria: string) => (
    <button
      type="button"
      className={styles.btnSecondary}
      style={{ minWidth: 44, minHeight: 44, padding: "6px 10px" }}
      aria-label={aria}
      onClick={() => onChange(shiftTime(value, delta))}
    >
      {label}
    </button>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {btn("−1h", -60, "One hour earlier")}
      {btn("−15m", -15, "Fifteen minutes earlier")}
      <span
        className="tnum"
        style={{ minWidth: 86, textAlign: "center", fontWeight: 700 }}
      >
        {fmtTimeLabel(value, h24)}
      </span>
      {btn("+15m", 15, "Fifteen minutes later")}
      {btn("+1h", 60, "One hour later")}
    </span>
  );
}

interface Props {
  schedule: AlarmClockSchedule;
  h24: boolean;
  variant: FrameVariant;
  onPatch: (p: UserSettingsPatch) => void;
  /** Start a manual session now. `durationMs` null = until stopped. */
  onStartNow: (durationMs: number | null) => void;
}

export function AlarmClockModeSection({
  schedule,
  h24,
  variant,
  onPatch,
  onStartNow,
}: Props) {
  const patch = (next: Partial<AlarmClockSchedule>) =>
    onPatch({ alarm_clock: { ...schedule, ...next } });

  const start = (durationMs: number | null) => {
    hapticSelect();
    onStartNow(durationMs);
  };

  return (
    <SubsectionCard
      title="Alarm clock mode"
      subtitle="Bedside clock face — glucose left, time right, both colored to the zone."
      icon={ClockIcon}
      accent="violet"
      headerRight={
        <Toggle
          on={schedule.enabled}
          onClick={() => patch({ enabled: !schedule.enabled })}
        />
      }
    >
      <div className={styles.actionRow} style={{ flexWrap: "wrap" }}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => start(null)}
        >
          Start now
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => start(3_600_000)}
        >
          1 hour
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => start(8 * 3_600_000)}
        >
          8 hours
        </button>
      </div>

      <Row label="Starts">
        {variant === "kiosk" ? (
          <KioskTimeStepper
            value={schedule.start}
            h24={h24}
            onChange={(next) => patch({ start: next })}
          />
        ) : (
          <input
            type="time"
            value={schedule.start}
            onChange={(e) => {
              if (e.target.value) patch({ start: e.target.value });
            }}
            style={{
              background: "var(--bg-sunk)",
              border: "0.5px solid var(--line-strong)",
              borderRadius: 8,
              color: "var(--ink)",
              padding: "8px 10px",
              fontFamily: "inherit",
            }}
          />
        )}
      </Row>
      <Row label="Ends">
        {variant === "kiosk" ? (
          <KioskTimeStepper
            value={schedule.end}
            h24={h24}
            onChange={(next) => patch({ end: next })}
          />
        ) : (
          <input
            type="time"
            value={schedule.end}
            onChange={(e) => {
              if (e.target.value) patch({ end: e.target.value });
            }}
            style={{
              background: "var(--bg-sunk)",
              border: "0.5px solid var(--line-strong)",
              borderRadius: 8,
              color: "var(--ink)",
              padding: "8px 10px",
              fontFamily: "inherit",
            }}
          />
        )}
      </Row>

      {/* Cycle 166: bedside-face ring visibility. Patches the whole
          sub-object like every other control here; the face is the only
          consumer — no other ring surface is touched by this. */}
      <Row label="Show insulin-on-board rings">
        <Toggle
          on={schedule.show_iob_rings ?? true}
          onClick={() =>
            patch({ show_iob_rings: !(schedule.show_iob_rings ?? true) })
          }
        />
      </Row>

      <Row label="Days">
        <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
          {DAY_LABELS.map((label, i) => {
            const on = schedule.days[i] === true;
            return (
              <button
                key={i}
                type="button"
                aria-pressed={on}
                aria-label={
                  [
                    "Sunday",
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                  ][i]
                }
                onClick={() => {
                  const days = schedule.days.slice();
                  days[i] = !on;
                  patch({ days });
                }}
                style={{
                  appearance: "none",
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: `0.5px solid ${on ? "var(--accent)" : "var(--line-strong)"}`,
                  background: on ? "var(--accent-soft)" : "var(--bg-sunk)",
                  color: on ? "var(--accent)" : "var(--ink-3)",
                  fontFamily: "inherit",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            );
          })}
        </span>
      </Row>

      <p className={styles.rowHint}>
        When enabled, the clock face starts and ends on its own during
        the window above (dimmed at night). Leave it anytime with the
        exit icon in its corner; come back with the Clock button in the
        nav or "Start now" here. Alarms always sound and display at full
        brightness on top of the clock.
      </p>
    </SubsectionCard>
  );
}
